<template>
  <div
    v-if="panels.length > 0"
    class="product-information container critical-component-clear"
  >
    <div class="grid product-information__wrapper">
      <div class="col xs-span l5">
        <responsive-image
          class="product-information__image"
          :image="productCompositionImage"
          ratio="1:1"
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </div>

      <div
        ref="accordion"
        class="col xs-span l7-12 product-information__accordion"
      >
        <accordion-snippet
          v-show="panels"
          content-modifier-classes="
            text-mobile-paragraph-m-regular
            text-desktop-paragraph-m-regular
          "
          heading-modifier-classes="text-mobile-headlines-h4 text-desktop-headlines-h4"
          heading-type="h2"
          :panel-data="panels"
          :scroll-to-active="false"
          @update-accordion-state="handleUpdateAccordionState"
        />
      </div>

      <teleport to="body">
        <overlay
          v-if="product && getlabAnalysisContent()"
          key="analysisModal"
          block-class="product-information__modal"
          direction="up"
          namespace="analysisModal"
          type="shelf-modal"
        >
          <template #title>
            <span
              v-text="$string('product.accordion.lab_analysis_modal_title')"
            />
          </template>

          <template #body>
            <div
              class="product-information__modal-content"
              js-overlay="scrollElement"
              v-html="getlabAnalysisModal()"
            />
          </template>
        </overlay>
      </teleport>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product information (product-information)
 * -----------------------------------------------------------------------------
 * Accordion on the PDP that contains product information.
 *
 * @param {Object} content - Content for panels.
 * @param {Object} product - Formatted product object.
 * @param {Boolean|Object} variant - Formatted variant object.
 *
 */
import {
  convertJsontoTable,
  getAssetUrls,
  modalWindow,
  updateTableContents,
} from '~/helpers/product-information';

import AccordionSnippet from '~async/accordion-snippet/accordion-snippet';
import Overlay from '~global/overlay/overlay';
import ResponsiveImage from '~global/images/responsive-image';

import { setIntersectionObserver } from '~/helpers/general';

export default {
  name: 'ProductInformation',

  components: {
    AccordionSnippet,
    Overlay,
    ResponsiveImage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({}),
    },
    content: {
      type: Object,
      default: () => ({
        accordionFallbackImage: '',
        applicationContent: '',
        compositionImage: '',
        description: '',
        descriptionFiles: [],
        goalChips: [],
        labAnalysisContent: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: () => ({}),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: () => ({}),
      required: true,
    },
  },

  data() {
    return {
      panelActive: {
        description: true,
        aminoAcid: false,
        nutrition: false,
        vitamin: false,
        mineral: false,
        application: false,
        labAnalysis: false,
      },
      panels: [],
    };
  },

  computed: {
    /**
     * Compute list of analysis files.
     * @returns {Array}
     */
    analysisFiles() {
      if (
        !this.product?.variants?.length ||
        location.pathname.includes('products_preview')
      ) {
        return [];
      }

      const files = this.product.variants.flatMap((variant) => {
        return variant.analysisFiles;
      });
      const uniqueFiles = [
        ...new Map(files.map((item) => [item.title, item])).values(),
      ];

      return uniqueFiles.sort((a, b) => {
        return new Date(b.dateOfAnalysis) - new Date(a.dateOfAnalysis);
      });
    },

    /**
     * Compute product composition image.
     * @returns {Object}
     */
    productCompositionImage() {
      return this.content.compositionImage?.src
        ? this.content.compositionImage
        : this.content.accordionFallbackImage;
    },
  },

  watch: {
    /**
     * Watch product to update panels.
     */
    product: {
      async handler() {
        this.buildPanels();

        await cnvs.Sleep(500);
        updateTableContents();
      },
      immediate: true,
    },

    /**
     * Watch variant to update panels.
     */
    variant: {
      async handler() {
        this.buildPanels();

        await cnvs.Sleep(500);
        updateTableContents();
      },
      immediate: true,
    },
  },

  mounted() {
    this.checkAccordionIntersection();
    modalWindow();
  },

  methods: {
    /**
     * Build panels
     */
    buildPanels() {
      if (!this.product) {
        return;
      }

      const panels = [];

      const buildPanels = [
        this.buildDescriptionPanel(),
        this.buildNutritionPanel(),
        this.buildVitaminPanel(),
        this.buildMineralPanel(),
        this.buildAminoAcidPanel(),
        this.buildApplicationPanel(),
        this.buildConsumptionRecommendationPanel(),
        this.buildlabAnalysisPanel(),
      ];

      buildPanels.forEach((value) => {
        if (!value.content) {
          return;
        }

        panels.push(value);
      });

      this.panels = panels;
    },

    /**
     * Returns formatted description panel.
     * @returns {Object}
     */
    buildDescriptionPanel() {
      const handle = 'description';
      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.description_title'),
        content: this.getDescription(),
      };
    },

    /**
     * Returns formatted nutrition panel.
     * @returns {Object}
     */
    buildNutritionPanel() {
      const handle = 'nutrition';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.nutritional_values'),
        content: this.getTableContent('nutritionValues'),
      };
    },

    /**
     * Returns formatted vitamins panel.
     * @returns {Object}
     */
    buildVitaminPanel() {
      const handle = 'vitamin';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.vitamin_values'),
        content: this.getTableContent('vitaminValues'),
      };
    },

    /**
     * Returns formatted minerals panel.
     * @returns {Object}
     */
    buildMineralPanel() {
      const handle = 'mineral';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.mineral_values'),
        content: this.getTableContent('mineralValues'),
      };
    },

    /**
     * Returns formatted application panel.
     * @returns {Object}
     */
    buildApplicationPanel() {
      const handle = 'application';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.application'),
        content: this.getApplicationContent(),
      };
    },

    /**
     * Returns formatted consumtionRecommendation panel.
     * @returns {Object}
     */
    buildConsumptionRecommendationPanel() {
      const handle = 'consumptionRecommendation';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.consumption_recommendation'),
        content: this.getConsumptionRecommendationContent(),
      };
    },

    /**
     * Returns formatted amino acid panel.
     * @returns {Object}
     */
    buildAminoAcidPanel() {
      const handle = 'aminoAcid';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.amino_acid_profile'),
        content: this.getAminoAcidContent(),
      };
    },

    /**
     * Returns formatted lab analysis panel.
     * @returns {Object}
     */
    buildlabAnalysisPanel() {
      const handle = 'labAnalysis';

      return {
        active: this.panelActive[handle],
        handle,
        title: this.$string('product.accordion.lab_analysis'),
        content: this.getlabAnalysisContent(),
      };
    },

    /**
     * Check if accordion is in view.
     */
    async checkAccordionIntersection() {
      if ('IntersectionObserver' in window === false) {
        return;
      }
      if (this.$refs.accordion) {
        setIntersectionObserver({
          callback: this.handleAccordionIntersection,
          element: this.$refs.accordion,
          rootMargin: '0px',
          threshold: 0.1,
        });
      }

      const isVisibleInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      // waiting for the next tick to ensure the accordion is rendered + a bit longer
      await this.$nextTick();
      await cnvs.Sleep(500);
      const labAnalysisTab = document.querySelector(
        '[data-handle="labAnalysis"]',
      );

      // if already visible on initial load, e.a when back button is pressed
      // after checking a PDF and labAnalysis tab is in viewport
      if (labAnalysisTab && isVisibleInViewport(labAnalysisTab)) {
        this.fetchPdfUrlsInBackground(
          this.analysisFiles.map((file) => file.url),
        );
      }
    },

    /**
     * Use parent product description, Goal Chips data from product metafield,
     * and PDF files from product metafield.
     * @returns {String}
     */
    getDescription() {
      const descriptionContent = [];

      /**
       * Get goal chips and create 'Goals' heading.
       */
      if (this.content.goalChips) {
        const goalChips = this.content.goalChips;

        const chips = goalChips
          .map((chip) => {
            return `
            <li
              class="
                goal-chips__badge
                text-mobile-paragraph-s-regular
                text-desktop-paragraph-s-regular
              "
            >
              ${chip}
            </li>
          `;
          })
          .join('');

        descriptionContent.push(
          `
            <h3
              class="
                text-mobile-paragraph-l-bold
                text-desktop-paragraph-l-bold
              "
            >
              ${this.$string('product.accordion.goals_title')}
            </h3>

            <ul class="goal-chips">${chips}</ul>
          `,
        );
      }

      /**
       * Get parent product description.
       */
      let description = '';

      if (this.content.description) {
        description = this.content.description;
        descriptionContent.push(this.formatContent(description));
      }

      /**
       * Get description PDFs.
       */
      if (this.content.descriptionFiles) {
        descriptionContent.push(
          `${this.getFilesList(this.content.descriptionFiles)}`,
        );
      }

      /**
       * Designation of food.
       */
      if (this.variant?.designationOfFood) {
        descriptionContent.push(`
          <h3 class="text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold">
            ${this.$string('product.accordion.designation_of_food')}
          </h3>

          <p class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular">
            ${this.variant.designationOfFood}
          </p>
        `);
      }

      /**
       * Ingredients
       */
      if (this.variant?.ingredients) {
        descriptionContent.push(`
          <h3 class="text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold">
            ${this.$string('product.accordion.ingredients')}
          </h3>

          ${this.formatContent(this.parseHtml(this.variant?.ingredients))}
        `);
      }

      /**
       * Sizes.
       */
      const localProduct = location.pathname.includes('products_preview')
        ? this.liquid
        : this.product;
      const sizesOption = localProduct?.options.find((option) => {
        return this.$variable('product.sizeOptions').includes(
          option.name.toLowerCase(),
        );
      });

      if (sizesOption && this.variant) {
        const availableProductVariants = localProduct.variants.filter(
          (variant) =>
            variant.selectedOptions[0].value ===
              this.variant.selectedOptions[0].value &&
            variant.availableState !== 'hidden',
        );
        if (availableProductVariants) {
          sizesOption.labels = availableProductVariants.map((item) => {
            if (item.selectedOptions.length > 1) {
              return [item.selectedOptions[1].value].join(' / ');
            }
            return [item.selectedOptions[0].value].join(' / ');
          });
        }

        descriptionContent.push(`
          <h3 class="text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold">
            ${this.$string('product.accordion.sizes')}
          </h3>

          <p class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular">
            ${sizesOption.labels.join(' / ')}
          </p>
        `);
      }

      return `<div>${descriptionContent.join('')}</div>`;
    },

    /**
     * Parses JSON to html structure
     * @returns {String}
     */
    parseHtml(text) {
      return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;nbsp;/g, '');
    },

    handleAccordionIntersection(entries) {
      entries.forEach((entry) => {
        if (
          !entry.isIntersecting ||
          this.analysisFiles.length === 0 ||
          entry.target.dataset.seen === 'true'
        ) {
          return;
        }

        this.fetchPdfUrlsInBackground(
          this.analysisFiles.map((file) => file.url),
        );

        entry.target.dataset.seen = 'true';
      });
    },

    async fetchPdfUrlsInBackground(gidList, batchSize = 50) {
      const pdfUrls = await getAssetUrls(gidList, batchSize);

      await cnvs.Sleep(500);
      if (this.analysisFiles.length === 0 || !pdfUrls) {
        return;
      }

      document.querySelectorAll('.fileslist__file a').forEach((file) => {
        const hrefAttribute = file.getAttribute('href');

        if (
          hrefAttribute &&
          Object.keys(pdfUrls).length > 0 &&
          pdfUrls[hrefAttribute]
        ) {
          file.setAttribute('href', pdfUrls[hrefAttribute]);
        }
      });
    },

    /**
     * Create template for the list of PDF files
     * used in the description and Lab Analysis tabs.
     * @param {Array} list - List of files.
     * @returns {String}
     */
    getFilesList(list) {
      const files = list
        .map((item) => {
          const parts = item.title.split('_');

          const year = parts[0];
          const month = parts[1];
          const day = parts[2];

          // Join the remaining parts with spaces
          const description = parts.slice(3).join(' ');

          // Format the new string
          const newTitle = `${day}.${month}.${year} ${description}`;
          const fileUrl = item.url;
          return `
          <li class="fileslist__file">
            <a
              class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
              download
              href="${fileUrl}"
            >
              <svg class="icon icon__pdf" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19.932 6.766c0-.243.212-.454.455-.454.272 0 .454.211.484.454v15.356c0 1.03-.848 1.878-1.878 1.878H5.878A1.888 1.888 0 0 1 4 22.122V1.89C4 .859 4.848.01 5.878.01h9.935c.122-.03.273 0 .394.12l4.483 4.485a.54.54 0 0 1 .091.515.473.473 0 0 1-.423.272h-3.272a1.68 1.68 0 0 1-1.666-1.666V.92H5.848a.944.944 0 0 0-.94.94v20.232c0 .516.425.94.94.94h13.145c.516 0 .94-.424.94-.94V6.767Zm-3.605-3.06a.75.75 0 0 0 .758.757h2.18l-2.938-2.937v2.18Z" fill-rule="evenodd" clip-rule="evenodd"/><path d="M11.997 5.797c0-.243.212-.454.454-.454.242 0 .455.211.515.483l.001 4.938L14.3 9.43c.152-.182.455-.182.637 0 .182.152.182.455 0 .637l-2.12 2.12a.461.461 0 0 1-.334.12.454.454 0 0 1-.333-.12l-2.12-2.12c-.183-.151-.183-.454 0-.637.15-.182.454-.182.636 0l1.332 1.333V5.797Z"/><path d="M8.725 17.004a.382.382 0 0 0-.394.394v2.786c0 .272.183.424.394.424.243 0 .394-.152.394-.424v-.757h.606c.788 0 1.212-.515 1.212-1.212 0-.817-.606-1.211-1.243-1.211h-.969Zm1.363 1.211c0 .243-.182.486-.545.486h-.424v-.97h.424c.334 0 .545.182.545.484ZM11.36 17.398c0-.243.183-.394.394-.394v-.03h.97c1.06 0 1.665.848 1.665 1.756 0 1.181-.788 1.817-1.666 1.817l-.969.002c-.211 0-.394-.122-.394-.394v-2.757Zm1.242 2.453c.636 0 .939-.424.939-1.06 0-.606-.303-1.03-.909-1.03h-.454v2.09h.423Z" fill-rule="evenodd" clip-rule="evenodd"/><path d="M16.51 17.761c.242 0 .393-.151.393-.363s-.151-.363-.394-.363h-1.242a.382.382 0 0 0-.394.394v2.786c0 .272.182.424.394.424.242 0 .394-.152.394-.424v-1.03h.726c.243 0 .394-.15.394-.362 0-.243-.151-.364-.394-.364h-.726v-.636l.848-.002v-.06ZM15.238 13.701c.242 0 .454.212.454.455a.446.446 0 0 1-.456.454H9.665a.466.466 0 0 1-.455-.454c0-.243.212-.455.455-.455h5.574Z"/></svg>

              <span class="fileslist__file-name">
                ${newTitle}
              </span>
            </a>
          </li>
        `;
        })
        .join('');

      return `<ul class="fileslist">${files}</ul>`;
    },

    /**
     * Get Application page content.
     * @returns {String}
     */
    getApplicationContent() {
      if (!this.content.applicationContent) {
        return '';
      }

      const applicationContent = [];
      applicationContent.push(
        this.formatContent(this.content.applicationContent),
      );

      return `<div>${applicationContent}</div>`;
    },

    /**
     * Populates table data accordions with metafield data, based on
     * variant metafield (nutrition, vitamin, mineral).
     * @returns {String} - Nutrition table.
     */
    getTableContent(key) {
      if (!key || !this.variant?.[key]) {
        return '';
      }

      const tableContent = [];

      if (this.variant?.[key]) {
        const tableData = convertJsontoTable(this.variant?.[key]);
        tableContent.push(tableData.outerHTML);
      }

      return `<div class="accordion-snippet__table">${tableContent.join('')}</div>`;
    },

    /**
     * Populates amino acid accordion with metafield data, based on
     * variant metafield.
     * @returns {String}
     */
    getAminoAcidContent() {
      if (!this.variant?.aminoAcidValues) {
        return '';
      }

      const aminoAcidContent = [];

      if (this.variant?.aminoAcidValues) {
        const tableData = convertJsontoTable(this.variant?.aminoAcidValues);
        aminoAcidContent.push(tableData.outerHTML);
      }

      return `<div class="accordion-snippet__table">${aminoAcidContent.join('')}</div>`;
    },

    /**
     * Populates consumption recommendation accordion with metafield data, based on
     * product metafield.
     * @returns {String}
     */
    getConsumptionRecommendationContent() {
      if (!this.product.consumptionRecommendation) {
        return '';
      }

      return this.product.consumptionRecommendation;
    },

    /**
     * Populates lab analysis accordion with metafield data, based on
     * variant metafields.
     * @returns {String}
     */
    getlabAnalysisContent() {
      if (!this.getLabAnalysisFiles().length) {
        return '';
      }

      const image = this.formatImage(this.content.labAnalysisContent.image);

      const labAnalysisContent = [];
      if (image) {
        labAnalysisContent.push(image);
      }
      labAnalysisContent.push(
        this.formatContent(this.content.labAnalysisContent.text),
      );
      if (this.content.labAnalysisContent.usps) {
        labAnalysisContent.push(this.content.labAnalysisContent.usps);
      }
      labAnalysisContent.push(`${this.getLabAnalysisFiles('short')}`);

      return `<div class='accordion-snippet__lab'>${labAnalysisContent.join('')}</div>`;
    },

    /**
     * Create the Lab Analysis modal overlay content.
     * @returns {String}
     */
    getlabAnalysisModal() {
      if (
        !this.content.labAnalysisContent ||
        !this.getLabAnalysisFiles().length
      ) {
        return '';
      }

      const labAnalysisModal = [];
      labAnalysisModal.push(`${this.getLabAnalysisFiles('long')}`);

      return `<div>${labAnalysisModal}</div>`;
    },

    /**
     * Get the Lab Analysis PDF files.
     * @param {String} type - Type of content, `short` or `long`.
     * @returns {String}
     */
    getLabAnalysisFiles(type) {
      const analysisFilesLength = this.analysisFiles.length;

      if (!analysisFilesLength) {
        return '';
      }

      if (type === 'short') {
        const fileTemplate = `
          <div class="product-information__lab-analysis">
            <h3 class="text-mobile-headlines-h6 text-desktop-headline-h6">
              ${this.$string('product.accordion.lab_analysis_files_title')}
            </h3>

            ${this.getFilesList(this.analysisFiles.slice(0, 3))}
          </div>
        `;

        const readMore = this.$string(
          'product.accordion.lab_analysis_see_more',
          {
            replace: {
              count: analysisFilesLength,
            },
          },
        );

        const seeMoreTemplate = `
          <div class="product-information__lab-analysis">
            ${fileTemplate}

            <button
              class="button button--text no-icon lab-analysis__button"
              js-accordion-content="button"
            >
              <span
                class="
                  button__label
                  text-mobile-paragraph-m-regular
                  text-desktop-paragraph-m-regular
                "
              >
                ${readMore}
              </span>
            </button>
          </div>
        `;

        return analysisFilesLength > 3 ? seeMoreTemplate : fileTemplate;
      }

      return this.getFilesList(this.analysisFiles);
    },

    formatImage(image) {
      if (Object.keys(image).length === 0) {
        return '';
      }

      return `
        <img
          class="product-information__lab-analysis-image"
          src="${image.src}"
          alt="${image.alt}"
        />
      `;
    },

    /**
     * Format content with standard classes.
     * @returns {String}
     */
    formatContent(content) {
      if (!content) {
        return '';
      }

      return content
        .replaceAll('<p><img', '<img')
        .replaceAll('alt></p>', 'alt>')
        .replaceAll('"></p>', '">')
        .replaceAll(
          '<p',
          '<p class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"',
        )
        .replaceAll(
          '<li',
          '<li class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"',
        )
        .replaceAll(
          '<a',
          '<a class="text-mobile-paragraph-m-bold-link text-desktop-paragraph-m-bold-link"',
        )
        .replaceAll(
          '<strong',
          '<strong class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"',
        )
        .replaceAll(
          '<h3>',
          '<h3 class="text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold">',
        )
        .replaceAll(
          '<th',
          '<th class="text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"',
        )
        .replaceAll(
          '<td',
          '<td class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"',
        )
        .replaceAll('<meta charset="utf-8">', '')
        .replaceAll('<ul data-mce-fragment="1"></ul>', '')
        .replaceAll(' data-mce-fragment="1"', '');
    },

    /**
     * Handle update accordion state.
     * @param {Object} response - State response.
     */
    handleUpdateAccordionState({ active, handle }) {
      this.panelActive[handle] = active;
    },
  },
};
</script>

<style lang="scss">
@import './product-information';
</style>
